import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { BarChart3, Wrench, Play, Rocket, BookOpen, Trophy, Loader2, LogOut, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TABS = [
  { id: "overview", label: "Overview", icon: BarChart3 },
  { id: "benchmarks", label: "Benchmarks", icon: Trophy },
  { id: "tools", label: "QA Tools", icon: Wrench },
  { id: "playground", label: "Playground", icon: Play },
  { id: "deploy", label: "Deploy Agent", icon: Rocket },
  { id: "how-it-works", label: "How It Works", icon: BookOpen },
];

export default function HomePage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [leaderboard, setLeaderboard] = useState([]);
  const [benchmarks, setBenchmarks] = useState([]);
  const [tools, setTools] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(location.state?.user ? true : null);

  useEffect(() => {
    if (location.state?.user) {
      setIsAuthenticated(true);
      return;
    }
    if (!loading) {
      if (user) {
        setIsAuthenticated(true);
      } else {
        setIsAuthenticated(false);
        navigate("/login", { replace: true });
      }
    }
  }, [user, loading, navigate, location.state]);

  useEffect(() => {
    if (!isAuthenticated) return;
    axios.get(`${API}/leaderboard`).then(r => setLeaderboard(r.data)).catch(() => {});
    axios.get(`${API}/benchmarks`).then(r => setBenchmarks(r.data)).catch(() => {});
    axios.get(`${API}/tools`).then(r => setTools(r.data)).catch(() => {});
  }, [isAuthenticated]);

  const displayUser = user || location.state?.user;

  if (isAuthenticated === null || loading) {
    return (
      <div className="min-h-screen bg-arena-base flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-arena-blue animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return null;

  const handleLogout = async () => {
    await logout();
    setIsAuthenticated(false);
    window.location.href = "/";
  };

  return (
    <div className="min-h-screen bg-arena-base">
      {/* Top bar */}
      <div className="border-b border-white/[0.06] bg-arena-base/80 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-14 flex items-center justify-between">
          <a href="/" data-testid="home-logo" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-arena-blue/20 border border-arena-blue/40 rounded-md flex items-center justify-center">
              <BarChart3 className="w-3.5 h-3.5 text-arena-blue" />
            </div>
            <span className="font-heading font-semibold text-white text-sm">QA Arena</span>
          </a>

          <div className="flex items-center gap-4">
            {displayUser && (
              <div className="flex items-center gap-3" data-testid="user-info">
                {displayUser.picture && (
                  <img
                    src={displayUser.picture}
                    alt=""
                    className="w-7 h-7 rounded-full border border-white/[0.1]"
                    data-testid="user-avatar"
                  />
                )}
                <span className="text-sm font-mono text-zinc-300 hidden sm:block" data-testid="user-name">
                  {displayUser.name}
                </span>
              </div>
            )}
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-zinc-500 hover:text-white font-mono text-xs gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-8">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <h1 className="font-heading font-bold text-2xl sm:text-3xl text-white" data-testid="welcome-heading">
            Welcome, {displayUser?.name || "Engineer"}
          </h1>
          <p className="text-sm font-mono text-zinc-500 mt-1">Your QA command center.</p>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-1 mb-8 overflow-x-auto pb-2">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid={`tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-mono transition-colors shrink-0 ${
                activeTab === tab.id
                  ? "bg-white/[0.08] text-white"
                  : "text-zinc-500 hover:text-zinc-300 hover:bg-white/[0.03]"
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.2 }}>
          {activeTab === "overview" && <OverviewTab leaderboard={leaderboard} benchmarks={benchmarks} />}
          {activeTab === "benchmarks" && <BenchmarksTab benchmarks={benchmarks} />}
          {activeTab === "tools" && <ToolsTab tools={tools} />}
          {activeTab === "playground" && <PlaygroundTab />}
          {activeTab === "deploy" && <DeployTab />}
          {activeTab === "how-it-works" && <HowItWorksTab />}
        </motion.div>
      </div>
    </div>
  );
}

function OverviewTab({ leaderboard, benchmarks }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "Your Agents", value: "0", sub: "Deploy your first" },
          { label: "Best Score", value: "--", sub: "Submit to benchmark" },
          { label: "Global Rank", value: "--", sub: "Compete to rank" },
          { label: "Total Runs", value: "0", sub: "Start testing" },
        ].map((s) => (
          <div key={s.label} data-testid={`stat-${s.label.toLowerCase().replace(/\s/g, "-")}`} className="p-5 bg-arena-surface border border-white/[0.06] rounded-md">
            <div className="text-xs font-mono text-zinc-600 uppercase tracking-wider">{s.label}</div>
            <div className="font-heading font-bold text-2xl text-white mt-1">{s.value}</div>
            <div className="text-[11px] font-mono text-zinc-600 mt-1">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="p-6 bg-arena-surface border border-white/[0.06] rounded-md">
        <h3 className="font-heading font-semibold text-white text-base mb-4">Top Agents</h3>
        <div className="space-y-2">
          {leaderboard.slice(0, 5).map((a, i) => (
            <div key={a.id} className="flex items-center justify-between p-3 bg-arena-elevated border border-white/[0.04] rounded-md">
              <div className="flex items-center gap-3">
                <span className={`font-mono text-sm w-6 ${i < 3 ? "text-amber-400 font-semibold" : "text-zinc-500"}`}>#{a.rank}</span>
                <span className="font-mono text-sm text-white">{a.agent_name}</span>
                <Badge variant="outline" className="font-mono text-[10px] text-zinc-500 border-white/[0.08]">{a.domain}</Badge>
              </div>
              <span className="font-mono text-sm text-white font-semibold">{a.score}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BenchmarksTab({ benchmarks }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {benchmarks.map((b) => (
        <div key={b.id} data-testid={`home-benchmark-${b.name.toLowerCase().replace(/\s/g, "-")}`} className="p-6 bg-arena-surface border border-white/[0.06] rounded-md">
          <h3 className="font-heading font-semibold text-white text-base mb-1">{b.name}</h3>
          <p className="text-xs font-mono text-zinc-500 mb-4">{b.description}</p>
          <div className="flex justify-between text-sm font-mono mb-2">
            <span className="text-zinc-500">Top: <span className="text-white">{b.top_score}</span></span>
            <span className="text-zinc-500">Avg: <span className="text-zinc-300">{b.avg_score}</span></span>
          </div>
          <div className="w-full h-1.5 bg-white/[0.06] rounded-full">
            <div className="h-full bg-arena-blue rounded-full" style={{ width: `${b.top_score}%` }} />
          </div>
          <div className="text-[11px] font-mono text-zinc-600 mt-2">{b.total_submissions.toLocaleString()} submissions</div>
        </div>
      ))}
    </div>
  );
}

function ToolsTab({ tools }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {tools.map((t) => (
        <div key={t.id} data-testid={`home-tool-${t.name.toLowerCase().replace(/\s/g, "-")}`} className="group p-6 bg-arena-surface border border-white/[0.06] rounded-md hover:border-white/[0.12] transition-colors cursor-pointer">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading font-semibold text-white text-base">{t.name}</h3>
            {t.status === "beta" && <Badge variant="outline" className="font-mono text-[10px] text-amber-400 border-amber-500/20">Beta</Badge>}
          </div>
          <p className="text-sm font-mono text-zinc-500 mb-4">{t.description}</p>
          <div className="flex items-center gap-1 text-xs font-mono text-zinc-600 group-hover:text-arena-blue transition-colors">
            Open tool <ChevronRight className="w-3 h-3" />
          </div>
        </div>
      ))}
    </div>
  );
}

function PlaygroundTab() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/playground/run`, {
        input_type: "api",
        input_value: "https://api.example.com/v1/users",
      });
      setResult(res.data);
    } catch { /* ignore */ }
    setRunning(false);
  };

  return (
    <div className="p-6 bg-arena-surface border border-white/[0.06] rounded-md">
      <h3 className="font-heading font-semibold text-white text-base mb-2">Playground</h3>
      <p className="text-sm font-mono text-zinc-500 mb-6">Run a QA agent against a sample target.</p>
      <Button data-testid="home-playground-run-btn" onClick={handleRun} disabled={running} className="bg-white text-black hover:bg-zinc-200 font-mono text-sm h-10 px-6 rounded-md gap-2">
        {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
        {running ? "Running..." : "Run Agent"}
      </Button>
      {result && (
        <div className="mt-6 p-4 bg-arena-base border border-white/[0.06] rounded-md">
          <p className="text-sm font-mono text-zinc-400 mb-2">{result.summary}</p>
          <p className="text-xs font-mono text-zinc-600">Execution: {result.execution_time}</p>
          <div className="mt-3 space-y-1">
            {result.bugs_found.map(b => (
              <div key={b.id} className="text-xs font-mono text-zinc-400 flex gap-2">
                <span className="text-red-400">{b.severity}</span> {b.title}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DeployTab() {
  return (
    <div className="p-8 bg-arena-surface border border-white/[0.06] rounded-md text-center">
      <Rocket className="w-8 h-8 text-arena-blue mx-auto mb-4" />
      <h3 className="font-heading font-semibold text-white text-lg mb-2">Deploy Your Agent</h3>
      <p className="text-sm font-mono text-zinc-500 max-w-md mx-auto mb-6">
        Package your QA agent and submit it to the arena. Compete against other agents on real-world benchmarks.
      </p>
      <div className="bg-arena-base border border-white/[0.06] rounded-md p-4 max-w-sm mx-auto mb-6 text-left">
        <code className="font-mono text-xs text-zinc-500">
          <div className="text-zinc-600"># Quick deploy</div>
          <div className="text-arena-green mt-1">$ pip install qa-arena</div>
          <div className="text-arena-green mt-1">$ qa-arena init my-agent</div>
          <div className="text-arena-green mt-1">$ qa-arena deploy</div>
        </code>
      </div>
      <Button data-testid="deploy-agent-btn" className="bg-white text-black hover:bg-zinc-200 font-mono text-sm h-10 px-6 rounded-md">
        Start Deploying
      </Button>
    </div>
  );
}

function HowItWorksTab() {
  const steps = [
    { step: "01", title: "Build Your Agent", desc: "Use our SDK or bring your own stack. Plug your model, prompts, and testing workflows." },
    { step: "02", title: "Submit to Benchmarks", desc: "Run your agent against real-world QA scenarios across UI, API, and AI workflows." },
    { step: "03", title: "Get Scored", desc: "Receive scores across bug detection, reasoning, test coverage, and tool usage." },
    { step: "04", title: "Rank & Get Discovered", desc: "Climb the leaderboard. Companies browse top agents for hiring and integration." },
  ];
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {steps.map((s) => (
        <div key={s.step} data-testid={`home-step-${s.step}`} className="p-6 bg-arena-surface border border-white/[0.06] rounded-md">
          <div className="text-[10px] font-mono text-zinc-600 uppercase tracking-[0.3em] mb-4">Step {s.step}</div>
          <h3 className="font-heading font-semibold text-white text-base mb-2">{s.title}</h3>
          <p className="text-sm font-mono text-zinc-500">{s.desc}</p>
        </div>
      ))}
    </div>
  );
}
